import React, { useRef, useState, useEffect } from 'react';
import { Input, InputCheckRules, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { Form } from '@components/atoms/Form';
import { Checkbox, CheckboxType } from '@components/atoms/Checkbox';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { useTranslation } from 'react-i18next';
import { useRegistrationPasswordStep } from '@services/useRegistrationPasswordStep';
import { useData } from '@hooks/useData';
import { useRoleChecker } from '@hooks/useRoleChecker';
import { useNavigate } from 'react-router-dom';
import { SITE_URLS } from '@constants';
import { useOutletContext } from 'react-router-dom';
import { useNotifications } from '@hooks/useNotifications';
import { Link } from 'react-router-dom';
import { Role } from '@types';
import styles from './PasswordStep.module.css';

interface PasswordStepContext {
    onBack: () => void;
    onNext: () => void;
}
export interface PasswordStepProps {
    fallback?: PasswordStepContext; // only used in Storybook
}

interface PasswordStepErrors {
    password: 'valid' | 'invalid' | null;
    confirmPassword: string[];
    termsAndConditions: string[];
}

export const PasswordStep: React.FC<PasswordStepProps> = ({ fallback }) => {
    const context = useOutletContext<PasswordStepContext | null>();
    const onBack = context?.onBack || fallback?.onBack || (() => console.log('Back clicked'));

    const { t } = useTranslation();
    const formRef = useRef<HTMLFormElement>(null);
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [validPassword, setValidPassword] = useState<boolean>(false);
    const [termsAndConditions, setTermsAndConditions] = useState<boolean>(false);
    const { showDefaultNotification } = useNotifications();
    const { request: requestRegisterPasswordStep, error, validationErrors, lastResponseTime } = useRegistrationPasswordStep();
    const [errors, setErrors] = useState<PasswordStepErrors>({
        password: null,
        confirmPassword: [],
        termsAndConditions: [],
    });

    const navigate = useNavigate();
    const { firstStep, roles } = useData();
    const draft = firstStep?.data || null;
    const draftRoleName = draft?.user_type_id ? roles?.data?.find((r: Role) => r.id === draft.user_type_id)?.name : undefined;
    const { isDriver } = useRoleChecker(draftRoleName?.toLowerCase());
    const submitLabel = draftRoleName && isDriver ? t('navigation.submitForValidation') : t('navigation.register');

    const translations = {
        title: t('password-form.title'),
        prev: t('navigation.prev'),
        passwordLabel: t('password-form.password.label'),
        confirmPasswordLabel: t('password-form.confirm-password.label'),
        termsAndConditionsLabel: t('password-form.terms-and-conditions.label'),
        termsAndConditionsLink: t('password-form.terms-and-conditions.link'),
        confirmPasswordRequiredError: t('password-form.confirm-password.required-error'),
        confirmPasswordMatchError: t('password-form.confirm-password.match-error'),
        didNotAcceptTermsAndConditionsError: t('password-form.terms-and-conditions.did-not-accept-error'),
    };

    const handleOnClickCreateAccount = () => {
        const errors = validateForm();

        if (errors.password === 'valid' && errors.confirmPassword.length === 0 && errors.termsAndConditions.length === 0) {
            formRef?.current?.requestSubmit();
        }
    };

    useEffect(() => {
        if (lastResponseTime) {
            if (validationErrors) {
                setErrors({
                    password: validationErrors['password'] && validationErrors['password'].length > 0 ? 'invalid' : 'valid',
                    confirmPassword: validationErrors['password.confirm'] || [],
                    termsAndConditions: [],
                });
            } else if (error) {
                showDefaultNotification({ type: 'error', message: error });
            } else {
                navigate(`/${SITE_URLS.login}`);
            }
        }
    }, [lastResponseTime]);

    const validateForm: () => PasswordStepErrors = () => {
        const newErrors: PasswordStepErrors = {
            password: validPassword ? 'valid' : 'invalid',
            confirmPassword: [],
            termsAndConditions: [],
        };

        if (newErrors.confirmPassword.length === 0 && password !== confirmPassword) {
            newErrors.confirmPassword.push(translations.confirmPasswordMatchError);
        }

        if (!termsAndConditions) {
            newErrors.termsAndConditions.push(translations.didNotAcceptTermsAndConditionsError);
        }

        setErrors(newErrors);
        return newErrors;
    };

    const submitForm: () => void = () => {
        requestRegisterPasswordStep({
            password: password,
            password_confirmation: confirmPassword,
        });
    };

    return (
        <Form
            formRef={formRef}
            classes="d-flex flex-column gap-2"
            onEnterPress={handleOnClickCreateAccount}
            onSubmit={() => submitForm()}>
            <>
                <Typography align="center" fontSize="16" fontWeight="400" color="muted">
                    {translations.title}
                </Typography>
                <Input
                    type={InputTypes.password}
                    variant={InputVariants.labeledInput}
                    label={`${translations.passwordLabel}*`}
                    value={password}
                    onChange={(value) => setPassword(value)}
                    state={errors.password === 'invalid' ? InputStates.error : undefined}
                    checkRules={InputCheckRules.passwordBuild}
                    onValidationChange={setValidPassword}
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                <Input
                    type={InputTypes.password}
                    variant={InputVariants.labeledInput}
                    label={`${translations.confirmPasswordLabel}*`}
                    value={confirmPassword}
                    onChange={(value) => setConfirmPassword(value)}
                    state={errors.confirmPassword.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.confirmPassword}
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                <Checkbox
                    type={CheckboxType.normal}
                    name="terms-and-conditions"
                    value="accept"
                    checked={termsAndConditions}
                    onChange={() => setTermsAndConditions(!termsAndConditions)}
                    label={
                        <Typography color="muted" fontSize="12">
                            <>
                                {`${translations.termsAndConditionsLabel} `}
                                <Link to="/terms-and-conditions" className={styles.no_underline}>
                                    <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                        {translations.termsAndConditionsLink}
                                    </Typography>
                                </Link>
                            </>
                        </Typography>
                    }
                />
                {errors.termsAndConditions.length > 0 && <ErrorMessages messages={errors.termsAndConditions} />}
                <Button onClick={onBack} label={translations.prev} mode={ButtonMode.primary} outline />
                <Button onClick={handleOnClickCreateAccount} label={submitLabel} mode={ButtonMode.primary} />
            </>
        </Form>
    );
};
